import { CameraKitSession, Lens } from "@snap/camera-kit";
import { Button, DropdownInput, Input, Option } from "@snapchat/snap-design-system";
import { CanvasContainer, Flexbox, FlexSection } from "shared-resources";
import { RenderSize } from "./hooks/useRenderSearchParams";
import {
    controlContainerSmallDevice,
    inputStyling,
    mainStyling,
    submitContainer,
    submitStyling,
} from "./UseCase.styles";

interface UseCaseProps {
    sessionOutput: CameraKitSession["output"];
    lenses: Lens[];
    customWidth: string | undefined;
    customHeight: string | undefined;
    onLensSelect: (lens: Lens) => Promise<void>;
    onSizeSelect: (size: RenderSize) => Promise<void>;
    onSubmit: () => Promise<void>;
    onCustomInputChange: (value: string, isWidth: boolean) => void;
}

export const UseCase = ({
    sessionOutput,
    lenses,
    customWidth,
    customHeight,
    onLensSelect,
    onSizeSelect,
    onSubmit,
    onCustomInputChange,
}: UseCaseProps) => {
    const renderMap: RenderSize[] = [
        { width: "640", height: "480" },
        { width: "320", height: "240" },
        { width: "160", height: "120" },
    ];
    const isValidWidth = customWidth && parseInt(customWidth) > 0 && parseInt(customWidth) <= 1200;
    const isValidHeight = customHeight && parseInt(customHeight) > 0 && parseInt(customHeight) <= 480;

    return (
        <Flexbox css={mainStyling} flexDirection="column">
            <FlexSection flexDirection="column" justifyContent="center">
                <Flexbox data-testid="render-size-dropdowns">
                    <DropdownInput
                        placeholder="Choose a Lens..."
                        label="Lens"
                        onChange={(lensId) => {
                            const lens = lenses.find((lens) => lens.id === lensId);
                            if (lens) onLensSelect(lens);
                        }}
                    >
                        {lenses.map((lens: Lens) => (
                            <Option key={lens.id} value={lens.id}>
                                {lens.name}
                            </Option>
                        ))}
                    </DropdownInput>
                </Flexbox>
                <Flexbox css={controlContainerSmallDevice}>
                    <DropdownInput
                        placeholder={"Predefined Sizes..."}
                        label="Render Size"
                        onChange={(renderMapIndex) => onSizeSelect(renderMap[renderMapIndex])}
                        data-testid="render-dropdown"
                    >
                        {renderMap.map((size, index) => (
                            <Option
                                key={`${size.width}x${size.height}`}
                                value={index}
                                data-testid={`${size.width}x${size.height}`}
                            >
                                {size.width} x {size.height}
                            </Option>
                        ))}
                    </DropdownInput>
                    <Input
                        css={inputStyling}
                        label="Width"
                        value={customWidth || ""}
                        data-testid="width-input"
                        tooltip="Max Width: 1200px"
                        onChange={(key) => onCustomInputChange(key, true)}
                    />
                    <Input
                        css={inputStyling}
                        label="Height"
                        value={customHeight || ""}
                        data-testid="height-input"
                        tooltip="Max Height: 480px"
                        onChange={(key) => onCustomInputChange(key, false)}
                    />
                    <div css={submitContainer}>
                        <Button
                            type="primary"
                            onClick={onSubmit}
                            isDisabled={!isValidHeight || !isValidWidth}
                            css={submitStyling}
                            data-testid="submit-button"
                        >
                            Submit
                        </Button>
                    </div>
                </Flexbox>
            </FlexSection>
            <FlexSection flexDirection="row" flexWrap="wrap">
                <CanvasContainer canvasOutput={sessionOutput.live} />
            </FlexSection>
        </Flexbox>
    );
};
